// Layer 3 (Transformations): Senior Metallurgist tab bodies — process
// health by area (§5a), cascade analysis (§5b), KB suggestions (§5d).
// Recovery figures read the seed series; attribution prose stays typed.

import type { SignalCell } from '@/versions/v1/schema/spc';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { readingsAt, seriesAt } from '@/versions/v1/shared/series.derive';
import { recoveryPtDelta } from '@/versions/v1/shared/recovery-vocab.derive';
import { formatDayLabel } from '@/versions/v1/shared/clock.derive';
import { spcRead } from '@/versions/v1/shared/SpcEngine';
import { smelterPenaltyLimit } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import { CONTROL_PLAN_ZN_ROWS } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
import { RECIRCULATING_LOOPS } from '@/versions/v1/shared/bunker-hill-seed';
import { dailyTrendSlice } from './LandingCursor.transform';
import { p80DesignMicrons, znGapPpt } from './LandingSeries.transform';
import { pbInZnTone } from './LimsTable.transform';

function spcCellTone(cell: SignalCell | undefined): 'ok' | 'warn' | 'bad' {
  if (cell === 'consequence' || cell === 'drift') return 'bad';
  if (cell === 'not-capable') return 'warn';
  return 'ok';
}

/** `neutral`: a reading with no seeded comparison — its value only, untoned (Thickener UF, adjudication 12). */
export type HealthMiniRow = { metric: string; value: string; vsTarget: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' };

export type HealthCardModel = {
  id: string;
  area: string;
  tone: 'ok' | 'warn' | 'bad';
  note: string;
  /** The short read set after the area name on the landing's area block
   *  (polish.P27 comp A): the fact the rows cannot carry. Default none. */
  tag?: string;
  rows: readonly HealthMiniRow[];
};

function readingValue(id: string, state: SessionState) {
  const projected = readingsAt(state).find((r) => r.id === id);
  const seed = readingByIdLookup(id);
  if (!seed) throw new Error(`Missing reading ${id}`);
  return projected ?? seed;
}

export function processHealthCards(state: SessionState): HealthCardModel[] {
  const zn = readingValue('rd-kpi-zn-rec', state);
  const pb = readingValue('rd-kpi-pb-rec', state);
  const znGrade = readingValue('rd-kpi-zn-grade', state);
  const pbGrade = readingValue('rd-kpi-pb-grade', state);
  const p80 = seriesAt('rs-sa01-p80', state).latest?.value ?? readingValue('rd-spec-p80', state).value ?? 82;
  const p80Design = p80DesignMicrons(state);
  const p80Tone = spcCellTone(spcRead('mv-sa01-p80', state).signal?.cell);
  const pbPh = readingValue('rd-kpv-pb-ph', state);
  const ph = pbPh.value!;
  const phBand = `${pbPh.band!.lo!.toFixed(1)}–${pbPh.band!.hi!.toFixed(1)}`;
  const phTone = ph < pbPh.band!.lo! || ph > pbPh.band!.hi! ? 'warn' : 'ok';
  const pbInZn = readingValue('rd-spec-zn-pb', state).value!;
  const pbLimit = smelterPenaltyLimit('Zn', 'Pb');
  // K10c item 2: the Zn pH excursion returns to the Zn Flotation card. The
  // value and tag read `rd-kpv-zn-ph` at the cursor; the band is the Control
  // Plan's own `cp-zn-ph` spec overlay — never a second typed band.
  const znPh = readingValue('rd-kpv-zn-ph', state);
  const znPhBand = CONTROL_PLAN_ZN_ROWS.find((row) => row.id === 'cp-zn-ph')!.specOverlay;
  const znPhTone: HealthMiniRow['tone'] =
    znPh.value! < znPhBand.lo! || znPh.value! > znPhBand.hi! ? 'warn' : 'ok';
  const feed = readingValue('rd-kpi-ore-td', state);
  const feedStph = feed.value! / 24;
  const feedDesignStph = feed.design! / 24;
  const millEnergy = readingValue('rd-jt023-kwpt', state);
  const moisture = readingValue('rd-unit-flzn-moist', state);
  const underflow = readingValue('rd-unit-tkzn-uf', state);
  const tailZnLimit = readingValue('rd-spec-tails-zn', state).spec!.hi!;
  const tailPbLimit = readingValue('rd-spec-tails-pb', state).spec!.hi!;
  const gap = znGapPpt(state);
  const cuso4Dose = seriesAt('rs-reag-cuso4', state).latest?.value ?? readingValue('rd-reag-cuso4', state).value ?? 155;
  const cuso4Design = readingValue('rd-reag-cuso4', state).design ?? 150;
  const cuso4Tone: HealthMiniRow['tone'] = cuso4Dose < cuso4Design ? 'warn' : 'ok';

  return [
    {
      id: 'hc-grind',
      area: 'Crushing and Grinding',
      tone: p80Tone === 'ok' ? 'warn' : p80Tone,
      note: `P80 ${p80} µm vs ${p80Design} µm design — feed ${feedStph.toFixed(1)} stph — BM01 bearing Tier 1`,
      tag: 'BM01 bearing · Tier 1',
      rows: [
        { metric: 'P80 (µm)', value: String(p80), vsTarget: `vs ${p80Design} design`, tone: p80Tone },
        { metric: 'Feed (stph)', value: feedStph.toFixed(1), vsTarget: `vs ${feedDesignStph.toFixed(1)} design`, tone: feedStph < feedDesignStph ? 'warn' : 'ok' },
        { metric: 'Circ. load (%)', value: RECIRCULATING_LOOPS.find((row) => row.id === 'loop-cyc')!.loadRatio.split('%')[0]!, vsTarget: 'vs 230–260', tone: 'ok' },
        { metric: 'Mill (kW/tph)', value: millEnergy.value!.toFixed(1), vsTarget: `vs ${millEnergy.design!.toFixed(1)} design`, tone: millEnergy.value! > millEnergy.design! ? 'warn' : 'ok' },
      ],
    },
    {
      id: 'hc-pbag',
      area: 'Pb/Ag Flotation',
      tone: 'warn',
      note: `Pb pH ${ph.toFixed(1)} vs ${phBand} band. Pb recovery ${pb.value?.toFixed(1)}% vs ${pb.design?.toFixed(1)}% design`,
      rows: [
        { metric: `Pb pH ${pbPh.tag}`, value: ph.toFixed(1), vsTarget: `vs ${phBand} band`, tone: phTone },
        { metric: 'Pb rec (%)', value: pb.value?.toFixed(1) ?? '—', vsTarget: `vs ${pb.design?.toFixed(1)} design`, tone: 'warn' },
        { metric: 'Pb grade (%)', value: pbGrade.value?.toFixed(1) ?? '—', vsTarget: 'vs min 62', tone: 'ok' },
      ],
    },
    {
      id: 'hc-zn',
      area: 'Zn Flotation',
      tone: 'bad',
      note: `Zn recovery ${zn.value?.toFixed(1)}% — ${gap.toFixed(1)} pt below design — three concurrent causes`,
      tag: 'three concurrent causes',
      rows: [
        { metric: 'Zn rec (%)', value: zn.value?.toFixed(1) ?? '—', vsTarget: `vs ${zn.design?.toFixed(1)} design`, tone: 'bad' },
        { metric: 'Zn grade (%)', value: znGrade.value?.toFixed(1) ?? '—', vsTarget: 'vs min 55', tone: 'ok' },
        { metric: 'Pb in Zn (%)', value: pbInZn.toFixed(1), vsTarget: `vs ${pbLimit.toFixed(1)} smelter limit`, tone: pbInZnTone(pbInZn) },
        // The excursion the compound-cause callout names, beside the reagent
        // that shares its pathway (a zinc-circuit reagent, moved off Pb/Ag).
        { metric: `Zn pH ${znPh.tag}`, value: znPh.value!.toFixed(1), vsTarget: `vs ${znPhBand.lo}–${znPhBand.hi} band`, tone: znPhTone },
        { metric: 'CuSO4 (g/t)', value: String(cuso4Dose), vsTarget: `vs ${cuso4Design}`, tone: cuso4Tone },
        { metric: 'SMBS ratio', value: '0.82', vsTarget: 'vs 0.86', tone: 'warn' },
      ],
    },
    {
      id: 'hc-dewater',
      area: 'Dewatering',
      tone: moisture.value! > moisture.design! ? 'warn' : 'ok',
      note: `Zn filter moisture ${moisture.value!.toFixed(1)}% vs ${moisture.design!.toFixed(1)}% design`,
      rows: [
        { metric: 'Con moisture (%)', value: moisture.value!.toFixed(1), vsTarget: `vs ${moisture.design!.toFixed(1)} design`, tone: moisture.value! > moisture.design! ? 'warn' : 'ok' },
        // `rd-unit-tkzn-uf` seeds no band (polish.P16 adjudication 12), so the row
        // reads its value with no comparison and no tone, as the Operator landing does.
        { metric: 'Thickener UF (%)', value: String(underflow.value!), vsTarget: '', tone: 'neutral' },
        { metric: 'Flocculant (g/t)', value: '15.2', vsTarget: 'vs 15', tone: 'ok' },
        { metric: 'Filter cycle (min)', value: '42', vsTarget: 'vs 40–45', tone: 'ok' },
      ],
    },
    {
      id: 'hc-tails',
      area: 'Tailings',
      tone: 'ok',
      note: 'Tailings system operating normally — flow and density in spec',
      tag: 'system operating normally',
      rows: [
        { metric: 'Tail Zn (%)', value: '0.42', vsTarget: `vs below ${tailZnLimit.toFixed(2)}`, tone: 'ok' },
        { metric: 'Tail Pb (%)', value: '0.28', vsTarget: `vs below ${tailPbLimit.toFixed(2)}`, tone: 'ok' },
        { metric: 'TSF level', value: 'Normal', vsTarget: 'vs normal', tone: 'ok' },
        { metric: 'Tail flow (m³/h)', value: '88', vsTarget: 'vs 80–95', tone: 'ok' },
      ],
    },
  ];
}

export type ProcessHealthInsight = {
  headline: string;
  /** The comparison the cause is consistent with (`2.8 pt gap`). */
  gapText: string;
  /** The fix sequence in the glass's order; no step is marked done, since no
   *  actual confirms one (comp adjudication 8). */
  steps: readonly { n: number; label: string; time: string }[];
};

/** The compound-cause insight that closes the area card (polish.P27 comp A,
 *  adjudication 8): an Engine-chip read with the fix sequence as numbered
 *  steps. The SMBS pathway sentence moved to the SMBS sign-off row. */
export function processHealthInsight(state: SessionState): ProcessHealthInsight {
  return {
    headline: 'Compound cause — upstream P80 and reagent issues are acting simultaneously on Zn recovery',
    gapText: `${znGapPpt(state).toFixed(1)} pt gap`,
    steps: [
      { n: 1, label: 'CuSO4', time: '~10:00' },
      { n: 2, label: 'Lime delivery', time: '21:00' },
      { n: 3, label: 'P80 reassess', time: '14:00' },
    ],
  };
}

export type CascadeRow = {
  id: string;
  /** The chart's own day labels (`Mon 02–Tue 03`, `Thu 05`; comp adjudication 5). */
  label: string;
  tone: 'ok' | 'warn' | 'bad';
  text: string;
  /** The category index the chart's numbered marker sits on. */
  markerIndex: number;
  /** The Zn recovery figure the row turns on, set in weight. Default none. */
  strong?: string;
};

export type CascadeAlignedRow = {
  day: string;
  ph: number;
  znRec: number;
  gapPpt: number;
};

/** Index-aligned daily slices — day n uses index n of every series. */
export function cascadeAlignedDailySlice(state: SessionState): CascadeAlignedRow[] {
  const znDaily = dailyTrendSlice('rs-zn-rec', state, 7);
  const phDaily = dailyTrendSlice('rs-zn-ph', state, 7);
  const znDesign = readingValue('rd-kpi-zn-rec', state).design ?? 85.1;
  return znDaily.categories.map((day, index) => ({
    day,
    ph: phDaily.points[index] ?? phDaily.points[phDaily.points.length - 1] ?? 0,
    znRec: znDaily.points[index] ?? znDaily.points[znDaily.points.length - 1] ?? 0,
    gapPpt: Math.abs(recoveryPtDelta(znDaily.points[index] ?? 0, znDesign)),
  }));
}

export function cascadeRows(state: SessionState): CascadeRow[] {
  const znDaily = dailyTrendSlice('rs-zn-rec', state, 7);
  const znDesign = readingValue('rd-kpi-zn-rec', state).design ?? 85.1;
  const p80Daily = dailyTrendSlice('rs-sa01-p80', state, 7);
  const phDaily = dailyTrendSlice('rs-zn-ph', state, 7);
  const p80Design = p80DesignMicrons(state);
  const feedDaily = dailyTrendSlice('rs-ore-td', state, 7);
  const feedAt = (index: number) => (feedDaily.points[index]! / 24).toFixed(1);
  const dayCount = znDaily.points.length;
  const znAt = (index: number) => znDaily.points[index] ?? znDaily.points[znDaily.points.length - 1] ?? 0;
  const phAt = (index: number) => phDaily.points[index] ?? phDaily.points[phDaily.points.length - 1] ?? 0;
  const p80At = (index: number) => p80Daily.points[index] ?? p80Daily.points[p80Daily.points.length - 1] ?? 0;
  const gapAt = (index: number) => Math.abs(recoveryPtDelta(znAt(index), znDesign));
  // Comp adjudication 5: each step is labelled with the chart's own day, and
  // the chart carries the same numeral on that day (`markerIndex`).
  const dayAt = (index: number) => (znDaily.stamps[index] ? formatDayLabel(znDaily.stamps[index]!) : '');
  const baselineEndIndex = Math.min(1, Math.max(0, dayCount - 1));
  const midZnIndex = Math.max(0, dayCount - 4);
  const limeDayIndex = Math.max(0, dayCount - 3);
  const cuso4DayIndex = Math.max(0, dayCount - 2);
  const lastIndex = Math.max(0, dayCount - 1);
  const phEarly = phAt(0);
  const phAtLimeDay = phAt(limeDayIndex);
  const phFall =
    phAtLimeDay < phEarly - 0.1
      ? `pH falls from ${phEarly.toFixed(1)} to ${phAtLimeDay.toFixed(1)}`
      : null;
  const zn = (index: number) => `${znAt(index).toFixed(1)}%`;
  return [
    {
      id: 'casc-1',
      label: `${dayAt(0)}–${dayAt(baselineEndIndex)}`,
      tone: 'ok',
      text: `Baseline — all KPIs near target. P80 ${p80At(0)} µm, pH ${phEarly.toFixed(1)}, CuSO4 dose stable.`,
      markerIndex: 0,
    },
    {
      id: 'casc-2',
      label: dayAt(midZnIndex),
      tone: 'warn',
      text: `P80 rises to ${p80At(midZnIndex)} µm (design ${p80Design} µm). Feed ${feedAt(0)} → ${feedAt(midZnIndex)} stph. First downstream effect: Zn recovery dips to ${zn(midZnIndex)}.`,
      markerIndex: midZnIndex,
      strong: zn(midZnIndex),
    },
    {
      id: 'casc-3',
      label: dayAt(limeDayIndex),
      tone: 'warn',
      text: phFall
        ? `Lime silo depletion event — ${phFall}. Zn recovery ${zn(limeDayIndex)} — two causes now acting simultaneously.`
        : `Lime silo depletion event — Zn recovery ${zn(limeDayIndex)} — two causes now acting simultaneously.`,
      markerIndex: limeDayIndex,
      strong: zn(limeDayIndex),
    },
    {
      id: 'casc-4',
      label: dayAt(cuso4DayIndex),
      tone: 'bad',
      text: `CuSO4 batch quality failure adds the third cause. P80 at ${p80At(cuso4DayIndex)} µm. Zn recovery ${zn(cuso4DayIndex)} — ${gapAt(cuso4DayIndex).toFixed(1)} pt below design.`,
      markerIndex: cuso4DayIndex,
      strong: zn(cuso4DayIndex),
    },
    {
      id: 'casc-5',
      label: dayAt(lastIndex),
      tone: 'bad',
      text: `All three causes persist. Zn recovery ${zn(lastIndex)} — ${gapAt(lastIndex).toFixed(1)} pt below design. Feed ${feedAt(lastIndex)} stph. Resolution: CuSO4 today, lime delivery tonight, P80 reassess 14:00.`,
      markerIndex: lastIndex,
      strong: zn(lastIndex),
    },
  ];
}

export type KbSuggestion = {
  id: string;
  type: string;
  title: string;
  confidencePct: number;
  confidenceTone: 'success' | 'warning' | 'default';
  confidenceLabel: string;
  evidence: string;
  status: string;
  statusTone: 'ok' | 'warn' | 'bad';
};

function kbConfidenceTone(pct: number): KbSuggestion['confidenceTone'] {
  if (pct >= 90) return 'success';
  if (pct >= 75) return 'warning';
  return 'default';
}

export function kbSuggestions(): KbSuggestion[] {
  return [
    {
      id: 'kb-1',
      type: 'TARP tightening',
      title: 'Zn flotation pH — reduce Tier 2 response time from 60 to 30 min',
      confidencePct: 84,
      confidenceTone: kbConfidenceTone(84),
      confidenceLabel: '84% conf.',
      evidence:
        '7 pH events analysed. In 6 of 7, delayed Tier 2 response (above 45 min) increased Zn rec impact by estimated 0.4 pt average.',
      status: 'Proposed — Met review',
      statusTone: 'warn',
    },
    {
      id: 'kb-2',
      type: 'FMEA update',
      title: 'CuSO4 batch — add batch age above 48h as sub-cause',
      confidencePct: 78,
      confidenceTone: kbConfidenceTone(78),
      confidenceLabel: '78% conf.',
      evidence:
        '3 of last 6 batch failures used material prepared more than 48h prior. Supplier TDS confirms strength degradation after 36–48h.',
      status: 'Proposed — verify with supplier',
      statusTone: 'warn',
    },
    {
      id: 'kb-3',
      type: 'Control Plan',
      title: 'AT-150 — increase calibration frequency to twice weekly',
      confidencePct: 91,
      confidenceTone: kbConfidenceTone(91),
      confidenceLabel: '91% conf.',
      evidence:
        'AT-150 drift above 0.3 pH detected 4 occasions in 8 weeks, all between weekly calibration cycles. Drift rate 0.05 pH per day exceeds check interval.',
      status: 'Approved — implementing this week',
      statusTone: 'ok',
    },
    {
      id: 'kb-4',
      type: 'SOP update',
      title: 'CuSO4 acceptance — add 180 g/L minimum acceptance criterion',
      confidencePct: 95,
      confidenceTone: kbConfidenceTone(95),
      confidenceLabel: '95% conf.',
      evidence: 'Three batch failures at 162–174 g/L. Current SOP has no minimum acceptance standard.',
      status: 'Implemented v3.2',
      statusTone: 'ok',
    },
    {
      id: 'kb-5',
      type: 'Procedure gap',
      title: 'No documented SPC tightening approval workflow',
      confidencePct: 88,
      confidenceTone: kbConfidenceTone(88),
      confidenceLabel: '88% conf.',
      evidence:
        'SPC engine proposed tightening on 3 variables in 30 days. No Met-approval process exists — decisions are ad hoc and not auditable.',
      status: 'Gap identified — draft needed',
      statusTone: 'bad',
    },
  ];
}
